<template>
  <div class="w-full rounded-2xl overflow-hidden" :class="[wrapperClass, variant === 'dark' ? 'bg-[#153838]/40 backdrop-blur-xl rounded-[24px] p-5 border border-white/5 shadow-lg text-white' : '']">
    <audio ref="audioRef" :src="src" preload="metadata" @timeupdate="onTimeUpdate" @loadedmetadata="onLoadedMetadata" @ended="onEnded" @play="isPlaying = true" @pause="isPlaying = false" />
    <div v-if="!src" class="py-4 text-center text-xs opacity-60" :class="variant === 'dark' ? 'text-white' : 'text-[#1c4848]/40'">No audio</div>
    <template v-else>
      <div class="flex flex-col gap-2 mb-4">
        <div class="flex items-end justify-between gap-[3px] h-8 px-1 opacity-80 cursor-pointer" @click="seekFromWaveform">
          <div
            v-for="(h, i) in barCount"
            :key="i"
            class="flex-1 min-w-[3px] rounded-full transition-colors duration-150"
            :class="i / barCount <= progress ? (variant === 'dark' ? 'bg-[#ecd79f]' : 'bg-[#ecd79f]') : (variant === 'dark' ? 'bg-white/20' : 'bg-[#1c4848]/20')"
            :style="{ height: barHeights[i] + '%' }"
          />
        </div>
        <div class="flex justify-between text-[9px] font-bold font-mono tracking-wider" :class="[timeClass, variant === 'dark' ? 'text-white/30' : 'text-[#1c4848]/30']">
          <span :class="variant === 'dark' ? (progress > 0 ? 'text-[#ecd79f]' : 'text-white/30') : (progress > 0 ? 'text-[#ecd79f]' : '')">{{ formattedCurrent }}</span>
          <span>{{ formattedDuration }}</span>
        </div>
      </div>
      <div class="flex items-center justify-between w-full">
        <div class="flex-1 flex items-center justify-start min-w-0">
          <button
            type="button"
            class="p-2 shrink-0 text-white/30 hover:text-white transition-colors"
            :class="variant !== 'dark' && '!text-[#1c4848]/50 hover:!text-[#1c4848]'"
            title="Restart"
            @click="restart"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="block"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          </button>
        </div>
        <div class="flex items-center justify-center gap-5 shrink-0 px-2">
          <button
            type="button"
            class="p-2 text-white/60 hover:text-white transition-colors"
            :class="variant !== 'dark' && '!text-[#1c4848]/60 hover:!text-[#1c4848]'"
            title="Skip back"
            @click="skipBack"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="block"><polygon points="11 17 6 12 11 7 11 17"/><polygon points="18 17 13 12 18 7 18 17"/></svg>
          </button>
          <button
            type="button"
            class="w-12 h-12 flex items-center justify-center bg-[#ecd79f] rounded-full text-[#1c4848] shadow-[0_4px_20px_rgba(236,215,159,0.4)] hover:scale-105 active:scale-95 transition-all shrink-0"
            @click="togglePlay"
          >
            <template v-if="isPlaying">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="block ml-0.5"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
            </template>
            <template v-else>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="block ml-0.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </template>
          </button>
          <button
            type="button"
            class="p-2 text-white/60 hover:text-white transition-colors"
            :class="variant !== 'dark' && '!text-[#1c4848]/60 hover:!text-[#1c4848]'"
            title="Skip next"
            @click="skipNext"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 17 18 12 13 7 13 17"/><polygon points="6 17 11 12 6 7 6 17"/></svg>
          </button>
        </div>
        <div class="flex-1 flex items-center justify-end min-w-0">
          <button
            type="button"
            class="p-2 shrink-0 text-white/30 hover:text-white transition-colors"
            :class="variant !== 'dark' && '!text-[#1c4848]/50 hover:!text-[#1c4848]'"
            title="Volume"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="block"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';

const props = defineProps({
  src: { type: String, default: '' },
  variant: { type: String, default: 'default' },
  wrapperClass: { type: String, default: '' },
  timeClass: { type: String, default: '' },
  barCount: { type: Number, default: 32 },
});

const audioRef = ref(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);

const progress = computed(() => (duration.value > 0 ? currentTime.value / duration.value : 0));

const formattedCurrent = computed(() => formatTime(currentTime.value));
const formattedDuration = computed(() => formatTime(duration.value));

const barHeights = ref([]);
function initBars() {
  const arr = [];
  for (let i = 0; i < props.barCount; i++) {
    arr.push(30 + Math.random() * 70);
  }
  barHeights.value = arr;
}
initBars();

watch(() => props.src, () => {
  currentTime.value = 0;
  duration.value = 0;
});

function formatTime(sec) {
  if (!sec || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function onTimeUpdate() {
  if (audioRef.value) currentTime.value = audioRef.value.currentTime;
}

function onLoadedMetadata() {
  if (audioRef.value) duration.value = audioRef.value.duration;
}

function onEnded() {
  isPlaying.value = false;
  currentTime.value = 0;
}

function togglePlay() {
  if (!audioRef.value) return;
  if (audioRef.value.paused) audioRef.value.play();
  else audioRef.value.pause();
}

function restart() {
  if (audioRef.value) {
    audioRef.value.currentTime = 0;
    if (!audioRef.value.paused) audioRef.value.play();
  }
}

function skipBack() {
  if (audioRef.value) audioRef.value.currentTime = Math.max(0, audioRef.value.currentTime - 10);
}

function skipNext() {
  if (audioRef.value) audioRef.value.currentTime = Math.min(audioRef.value.duration, audioRef.value.currentTime + 10);
}

function seekFromWaveform(ev) {
  if (!audioRef.value || duration.value <= 0) return;
  const el = ev.currentTarget;
  const rect = el.getBoundingClientRect();
  const x = ev.clientX - rect.left;
  const p = x / rect.width;
  audioRef.value.currentTime = p * duration.value;
  currentTime.value = audioRef.value.currentTime;
}

onUnmounted(() => {
  if (audioRef.value) {
    audioRef.value.pause();
  }
});
</script>
